import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { AuthServiceHelper } from '@/modules/Auth'
import { useEMTDispatch } from '@/store'
import { Routes } from '@/assets/configs'
import useConnectionRoutes from '@/hooks/useConnectionRoutes'
import { UrlParamType } from '@/modules/utils'
import { appendParamsIfNeeded, getFirstQueryParam, openUrlApp } from '@/utils'
import { Constants } from '@/assets'
import { useFirstRouterQueryParam, useIsResponsive } from '@/hooks'
import GlobalLoader from '@/components/GlobalLoader'
import WelcomeLoader from '@/components/WelcomeLoader'
import { UserServiceHelper } from '@/modules/User'
import { RegistrationType } from '@/dataV3/User/ConnectedUser'
import useCurrentUser from '@/hooks/useCurrentUser'
import usePetSitterConnectionRoutes from '@/hooks/usePetSitterConnectionRoutes'
import { useUIStore } from '@/store/ui/uiStore'
import useRNWebViewTranslation from '@/hooks/useRNWebViewTranslation'
import { usePetsittingMode } from '@/store/usePetsittingMode'
import { _saveItem, TokenKeys } from '@/modules/Auth/LocalStorageHelper'
import { refreshTokenAsync } from '@/modules/Auth/BasicAuthServiceHelper'

export type withPrivateRouteProps = unknown

const withPrivateRoute =
  (redirectPath = Routes.Connection.SignIn) =>
  <Config,>(WrappedComponent: React.FC<Config & withPrivateRouteProps>) => {
    const PrivateRoute: React.FC<Config> = (props) => {
      const router = useRouter()
      const dispatch = useEMTDispatch()

      const { currentUnknownUser } = useCurrentUser()
      const { getNextRoute, checkConnectionRoute } = useConnectionRoutes()
      const { goNextStep, checkPetsitterConnectionRoute } = usePetSitterConnectionRoutes({ shouldInitialize: false })

      const { setDisconnectionModalOpen } = useUIStore()
      const [isMobile] = useIsResponsive()
      const hasRNWebView = useRNWebViewTranslation()
      const { isPetsittingMode } = usePetsittingMode()

      const [queryType, querySSOEmail, querySSOToken, queryToken, queryRefreshToken] = useFirstRouterQueryParam([
        'type',
        'email',
        'sso',
        'token',
        'refreshToken',
      ])

      const [isLoading, setLoading] = useState(true)

      // -------------------------
      // 🔒 Guards importants
      // -------------------------
      const hasRedirectedRef = useRef(false)
      const hasAttemptedSSO = useRef(false)
      const isFirstLaunch = useRef(true)
      const intendedPathRef = useRef<string>(router.asPath)
      const hasCleanedTokens = useRef(false)

      const safeRedirect = useCallback(
        (url: string) => {
          if (hasRedirectedRef.current) return
          hasRedirectedRef.current = true
          router.replace(url)
        },
        [router],
      )

      const cleanupSSOQueryParams = useCallback(() => {
        if (!router.query.email && !router.query.sso) return

        const cleanedQuery = { ...router.query }
        delete cleanedQuery.email
        delete cleanedQuery.sso

        router.replace(
          {
            pathname: router.pathname,
            query: cleanedQuery,
          },
          undefined,
          { shallow: true },
        )
      }, [router])

      // -------------------------
      // Redirect path adaptation
      // -------------------------
      if (redirectPath === Routes.Connection.SignIn && isMobile && hasRNWebView && !isPetsittingMode) {
        redirectPath = Routes.Connection.Welcome
      }

      if (redirectPath === Routes.Connection.SignIn && isPetsittingMode) {
        redirectPath = Routes.Connection.WelcomePetsitting
      }

      const checkProRoute = useCallback((pathname: string) => {
        return pathname === '/pro' || pathname.startsWith('/pro/')
      }, [])

      // -------------------------
      // AUTH FLOW
      // -------------------------
      useEffect(() => {
        const run = async () => {
          try {
            if (hasRedirectedRef.current) return

            // 👉 fix important: garder la route initiale SSO
            if (!intendedPathRef.current || intendedPathRef.current === Routes.Connection.SignIn) {
              intendedPathRef.current = router.asPath
            }

            const realRouterPathname = Object.keys(router.query).reduce((final, key) => {
              const value = getFirstQueryParam(router.query[key])
              if (!value) return final
              return final.replace(new RegExp(`\\[${key}\\]`, 'gm'), value)
            }, router.pathname)

            // -------------------------
            // Save tokens if present
            // -------------------------
            if ((queryToken || queryRefreshToken) && !hasCleanedTokens.current) {
              hasCleanedTokens.current = true
              if (queryToken) _saveItem(TokenKeys.Auth, queryToken)

              if (queryRefreshToken) {
                _saveItem(TokenKeys.Refresh, queryRefreshToken)
                refreshTokenAsync(queryRefreshToken)
              }

              // 🔥 Remove tokens from URL
              const cleanedQuery = { ...router.query }
              delete cleanedQuery.token
              delete cleanedQuery.refreshToken

              router.replace(
                {
                  pathname: router.pathname,
                  query: cleanedQuery,
                },
                undefined,
                { shallow: true },
              )

              return
            }

            // -------------------------
            // AUTH CHECK
            // -------------------------
            let finalUser = currentUnknownUser

            const refreshUser = isFirstLaunch.current
            let { isValid: logged, ...result } = await AuthServiceHelper.dispatchCheckAuthStatusAsync(dispatch)(
              refreshUser,
            )

            isFirstLaunch.current = false

            if (refreshUser) finalUser = result.user

            // -------------------------
            // SSO logout/login handling
            // -------------------------
            if (querySSOEmail && querySSOToken && !hasAttemptedSSO.current) {
              hasAttemptedSSO.current = true

              const normalizedSSOEmail = querySSOEmail.replace(' ', '+').trim().toLowerCase()
              const currentEmailRaw = finalUser ? (finalUser as unknown as Record<string, unknown>)['email'] : undefined
              const normalizedCurrentEmail =
                typeof currentEmailRaw === 'string' ? currentEmailRaw.trim().toLowerCase() : ''

              const shouldSwitchAccount =
                !!logged && !!finalUser && !!normalizedCurrentEmail && normalizedCurrentEmail !== normalizedSSOEmail

              if (shouldSwitchAccount) {
                await AuthServiceHelper.dispatchLogoutAsync(dispatch)()
                logged = false
                finalUser = null
              }

              if (!logged || !finalUser) {
                try {
                  finalUser = await AuthServiceHelper.dispatchLoginAsync(dispatch)({
                    email: querySSOEmail.replace(' ', '+'),
                    sso: querySSOToken,
                  })
                  logged = true
                } catch {
                  logged = false
                }
              }

              cleanupSSOQueryParams()
            }

            // -------------------------
            // NOT LOGGED
            // -------------------------
            if (!logged) {
              if (finalUser) {
                setDisconnectionModalOpen?.(true)
                return
              }

              safeRedirect(
                appendParamsIfNeeded(redirectPath, {
                  type: queryType,
                  nextPage: intendedPathRef.current,
                }),
              )
              return
            }

            // -------------------------
            // ENSURE USER
            // -------------------------
            if (!finalUser) {
              finalUser = await UserServiceHelper.dispatchGetMyInfoAsync(dispatch)({
                reactivation: false,
              })

              if (!finalUser) {
                await AuthServiceHelper.dispatchLogoutAsync(dispatch)()
                return
              }
            }

            // -------------------------
            // CONNECTION ROUTES
            // -------------------------
            const isConnectionRoute = checkConnectionRoute(router.pathname)
            const isPetSitterConnectionRoute = checkPetsitterConnectionRoute(router.pathname)

            if (isConnectionRoute || isPetSitterConnectionRoute) {
              setLoading(false)
              return
            }

            // -------------------------
            // ROLE LOGIC
            // -------------------------
            const isPetSitter = finalUser.registrationType === RegistrationType.petsitter
            const isOwner = finalUser.registrationType === RegistrationType.petowner
            const isBorrower = finalUser.registrationType === RegistrationType.borrower

            const isCurrentRoutePro = checkProRoute(realRouterPathname)

            let loggedType: string = UrlParamType.Borrower
            if (isPetSitter) loggedType = UrlParamType.Pro
            else if (isOwner) loggedType = UrlParamType.Owner

            // -------------------------
            // PETSITTING MODE EDGE CASE
            // -------------------------
            if (isBorrower && isPetsittingMode) {
              openUrlApp(
                `https://app.empruntemontoutou.com/?token=${encodeURIComponent(
                  localStorage.getItem(TokenKeys.Auth ?? '') || '',
                )}&refreshToken=${encodeURIComponent(
                  localStorage.getItem(TokenKeys.Refresh ?? '') || '',
                )}&type=borrower`,
              )

              await AuthServiceHelper.dispatchLogoutAsync(dispatch)()
              setLoading(false)
              return
            }

            // -------------------------
            // PRO ROUTES REDIRECTS
            // -------------------------
            if (isPetSitter && !isCurrentRoutePro) {
              safeRedirect(
                appendParamsIfNeeded(Routes.Pro.Dashboard, {
                  type: loggedType,
                  nextPage: intendedPathRef.current,
                }),
              )
              return
            }

            if (!isPetSitter && isCurrentRoutePro) {
              const nextRoute = getNextRoute(finalUser, {
                nextPage: intendedPathRef.current,
                fromRegister: router.pathname.includes('create-your-profile') || router.pathname.includes('sign-up'),
              })

              const finalRoute = appendParamsIfNeeded(new URL(nextRoute, Constants.apiUrl).pathname, {
                type: loggedType,
                nextPage: intendedPathRef.current,
              })

              safeRedirect(finalRoute)
              return
            }

            // -------------------------
            // ACQUISITION USERS
            // -------------------------
            if (finalUser['@type'] === 'PendingAcquisitionUser') {
              const next = getNextRoute(finalUser)
              if (next && next !== router.asPath) {
                safeRedirect(next)
                return
              }
            }

            if (finalUser['@type'] === 'PendingAcquisitionPetsitter') {
              goNextStep()
              return
            }

            // -------------------------
            // FIX TYPE QUERY
            // -------------------------
            if (queryType !== loggedType) {
              router.replace({
                pathname: router.pathname,
                query: { ...router.query, type: loggedType },
              })
            }

            setLoading(false)
          } catch (err) {
            console.debug(err)
            setLoading(false)
          }
        }

        run()
      }, [
        router.pathname,
        querySSOEmail,
        querySSOToken,
        queryToken,
        queryRefreshToken,
        queryType,
        cleanupSSOQueryParams,
      ])

      // -------------------------
      // LOADER
      // -------------------------
      if (isLoading) {
        return router.pathname !== Routes.Connection.Welcome ? <GlobalLoader /> : <WelcomeLoader />
      }

      // @ts-expect-error: needs to extend IntrinsicAttributes
      return <WrappedComponent {...props} />
    }

    return PrivateRoute
  }

export default withPrivateRoute
